import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import { rolesApi } from '../api/roles';
import { useToast } from '../hooks/useToast';

const RoleManagement: React.FC = () => {
  const { showToast } = useToast();
  const [roles, setRoles] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<Record<string, any[]>>({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', scope: 'branch' as 'company' | 'branch' });
  const [selectedPerms, setSelectedPerms] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    rolesApi.list().then(r => setRoles(r.data.data));
    rolesApi.permissionsCatalog().then(r => setCatalog(r.data.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', description: '', scope: 'branch' });
    setSelectedPerms(new Set());
    setShowForm(true);
  };

  const openEdit = async (role: any) => {
    setEditingId(role.id);
    setForm({ name: role.name, description: role.description || '', scope: role.scope });
    const res = await rolesApi.show(role.id);
    setSelectedPerms(new Set(res.data.data.permission_ids));
    setShowForm(true);
  };

  const togglePerm = (id: number) => {
    setSelectedPerms(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleModule = (moduleKey: string, perms: any[]) => {
    const allSelected = perms.every(p => selectedPerms.has(p.id));
    setSelectedPerms(prev => {
      const next = new Set(prev);
      perms.forEach(p => { allSelected ? next.delete(p.id) : next.add(p.id); });
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('Role name required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, permission_ids: Array.from(selectedPerms) };
      if (editingId) {
        await rolesApi.update(editingId, payload);
        showToast('Role updated');
      } else {
        await rolesApi.create(payload);
        showToast('Role created');
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: any) => {
    if (role.is_system) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await rolesApi.destroy(role.id);
      showToast('Role deleted');
      load();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx2)' }}>Roles</span>
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={13} /> New Role</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span>{editingId ? 'Edit Role' : 'New Role'}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-control" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Scope *</label>
                <select className="form-control" value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value as any }))}>
                  <option value="branch">Branch (single branch)</option>
                  <option value="company">Company (all branches)</option>
                </select>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx2)', marginBottom: 8 }}>Permissions</div>
            <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--bdr)', borderRadius: 6, padding: 12 }}>
              {Object.entries(catalog).map(([moduleKey, perms]) => {
                const allSelected = perms.every((p: any) => selectedPerms.has(p.id));
                return (
                  <div key={moduleKey} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--tx3)', marginBottom: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={allSelected} onChange={() => toggleModule(moduleKey, perms)} />
                      {moduleKey.replace(/_/g, ' ')}
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, paddingLeft: 22 }}>
                      {perms.map((p: any) => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={selectedPerms.has(p.id)} onChange={() => togglePerm(p.id)} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              {Object.keys(catalog).length === 0 && <div style={{ fontSize: 12, color: 'var(--tx3)' }}>No permissions found.</div>}
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={13} /> {saving ? 'Saving...' : 'Save Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Scope</th>
              <th>Type</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td style={{ color: 'var(--tx3)' }}>{r.description || '—'}</td>
                <td><span className="badge badge-blue">{r.scope}</span></td>
                <td>{r.is_system ? <span className="badge badge-gray">System</span> : <span className="badge badge-green">Custom</span>}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)} style={{ marginRight: 6 }}>Edit</button>
                  {!r.is_system && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(r)}><Trash2 size={12} /></button>
                  )}
                </td>
              </tr>
            ))}
            {roles.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--tx3)' }}>No roles yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoleManagement;
